import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { transactionService } from '../services/api';

const PAYMENT_TIMEOUT_SECONDS = 60;

const QRPaymentFlow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'loading' | 'waiting' | 'completed' | 'failed'>('loading');
  const [message, setMessage] = useState<string>('Loading payment...');
  const [countdown, setCountdown] = useState(PAYMENT_TIMEOUT_SECONDS);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const transactionId = location.state?.transactionId;
  const amount = location.state?.amount || 0;
  const ticketType = location.state?.ticketType;
  const paymentData = location.state?.paymentData;
  const qrCodeUrl = paymentData?.qr_code_url;
  const testingMode = location.state?.testingMode || false;

  const clearTimers = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!transactionId || !qrCodeUrl) {
      console.error('[QRPaymentFlow] Missing transaction data');
      setStatus('failed');
      setMessage('Missing payment information. Please try again.');
      return;
    }

    console.log('[QRPaymentFlow] Starting QR payment flow:', {
      transactionId,
      amount,
      qrCodeUrl,
    });

    setStatus('waiting');
    setMessage('Scan the QR code with your Payconiq app');

    // Start countdown timer
    timerIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Timeout reached - silently redirect home
          clearTimers();
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Start polling for payment status
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const transactionData = await transactionService.getStatus(transactionId);
        console.log('[QRPaymentFlow] Poll result:', transactionData.status);

        if (transactionData.status === 'completed') {
          clearTimers();
          setStatus('completed');
          setMessage('Payment successful! Loading your ticket...');

          // Fetch the ticket
          const ticketData = await transactionService.getTicket(transactionId);

          // Navigate to receipt page - QR flow doesn't show ticket (user has it on phone)
          navigate('/receipt', {
            state: {
              transactionId,
              paymentFlow: 'qr', // Indicates user already has ticket on phone
            },
            replace: true,
          });
        } else if (transactionData.status === 'failed') {
          clearTimers();
          setStatus('failed');
          setMessage('Payment failed. Please try again.');
        }
      } catch (err) {
        console.error('[QRPaymentFlow] Polling error:', err);
      }
    }, 3000);

    return () => {
      clearTimers();
    };
  }, [transactionId, qrCodeUrl, amount, clearTimers, navigate]);

  const handleCancel = () => {
    clearTimers();
    navigate('/');
  };

  const handleTestComplete = async () => {
    try {
      console.log('[QRPaymentFlow] Test completing transaction:', transactionId);
      await transactionService.testComplete(transactionId);
      clearTimers();
      setStatus('completed');
      setMessage('Payment marked as complete (test mode)');

      // Navigate to receipt page - pass testingMode to show ticket QR
      navigate('/receipt', {
        state: {
          transactionId,
          paymentFlow: 'qr',
          testingMode: true, // Show ticket QR so tester can scan it
        },
        replace: true,
      });
    } catch (err) {
      console.error('[QRPaymentFlow] Test complete failed:', err);
      setMessage('Failed to complete test payment');
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/20">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {status === 'loading' && (
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
              </div>
            )}

            {status === 'waiting' && (
              <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
              </div>
            )}

            {status === 'completed' && (
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}

            {status === 'failed' && (
              <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}

          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
            {status === 'loading' && 'Loading Payment'}
            {status === 'waiting' && 'Scan to Pay'}
            {status === 'completed' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Failed'}
          </h2>

          {/* Message */}
          <p className="text-gray-600 text-center mb-6">{message}</p>

          {/* Amount */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Amount</span>
              <span className="text-2xl font-bold text-gray-900">&euro;{parseFloat(String(amount)).toFixed(2)}</span>
            </div>
            {ticketType?.name && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Ticket</span>
                <span className="text-sm font-medium text-gray-700">{ticketType.name}</span>
              </div>
            )}
          </div>

          {/* QR Code for waiting state */}
          {status === 'waiting' && qrCodeUrl && (
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-400 rounded-2xl p-6 mb-4">
              <div className="text-center">
                <div className="bg-white p-4 rounded-xl border-2 border-orange-300 inline-block mb-4 shadow-lg">
                  <img
                    src={qrCodeUrl}
                    alt="Payconiq Payment QR Code"
                    className="w-48 h-48 mx-auto"
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-orange-800 font-semibold">
                    Open Payconiq app and scan this code
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-orange-600 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-600 border-t-transparent"></div>
                    <span>Waiting for payment...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Countdown timer */}
          {status === 'waiting' && (
            <div className="text-center mb-6">
              <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${countdown <= 10 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold">{formatTime(countdown)}</span>
                <span className="text-sm">remaining</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col space-y-3">
            {/* Test Mode: Mark as Paid button */}
            {testingMode && status === 'waiting' && (
              <button
                onClick={handleTestComplete}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Mark as Paid (Test Mode)</span>
              </button>
            )}

            {status === 'failed' && (
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                Try Again
              </button>
            )}

            {(status === 'loading' || status === 'waiting' || status === 'failed') && (
              <button
                onClick={handleCancel}
                className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-2 text-gray-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
              <span className="text-xs font-medium">Payconiq Payment</span>
              {testingMode && (
                <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-semibold">
                  TEST MODE
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRPaymentFlow;
